import type {
  QuestionnaireItemAnswerOptionWithTranslations,
  QuestionnaireItemWithTranslations,
  QuestionnaireWithTranslations,
} from '@liza/fhir-utils';
import { MOOD_SCALE_SYSTEM } from '../codeSystem/moodScale';
import { SURVEY_SCORES_SYSTEM } from '../codeSystem/surveyScores';
import {
  buildScoreObservationTemplate,
  buildTemplateExtractExtension,
  ORDINAL_VALUE_URL,
  OPTION_PREFIX_URL,
  SEMVER_VERSION_ALGORITHM_EXTENSION,
  withEs,
} from './scoredSurvey';

/**
 * Daily Mood Scale — an internal single-item "how is your mood today?" check on a 1–5 scale. Unlike
 * PHQ-9/GAD-7 it is not a validated, LOINC-coded instrument, so its answer options and total-score
 * result code live on minted in-house CodeSystems (`mood-scale`, `survey-scores`) rather than LOINC.
 * The selected ordinal (1–5) *is* the score — a single-term `$extract` sum. The instrument is
 * identified by its canonical `url`; it carries no `Questionnaire.code` (nothing keys on it). See the
 * survey FHIR design-decision doc, and `src/__tests__/liveMedplum/moodScaleExtractScoring.test.ts`.
 *
 * Wording confirmed for the pilot: EN "How would you describe your mood today?",
 * ES "¿Cómo describiría su estado de ánimo hoy?".
 */

/** The minted total-score code carried by the result Observation / Goal / Task (on `survey-scores`). */
export const MOOD_SCALE_RESULT_CODE = 'mood-scale';

const moodAnswerOption = (
  ordinal: number,
  code: string,
  display: string,
  displayEs: string,
): QuestionnaireItemAnswerOptionWithTranslations => ({
  extension: [
    { url: OPTION_PREFIX_URL, valueString: String(ordinal) },
    { url: ORDINAL_VALUE_URL, valueDecimal: ordinal },
  ],
  valueCoding: { system: MOOD_SCALE_SYSTEM, code, display, _display: withEs(displayEs) },
});

const moodAnswerOptions: QuestionnaireItemAnswerOptionWithTranslations[] = [
  moodAnswerOption(1, 'very-bad', 'Very bad', 'Muy mal'),
  moodAnswerOption(2, 'bad', 'Bad', 'Mal'),
  moodAnswerOption(3, 'neutral', 'Neutral', 'Neutral'),
  moodAnswerOption(4, 'good', 'Good', 'Bien'),
  moodAnswerOption(5, 'very-good', 'Very good', 'Muy bien'),
];

const moodItem: QuestionnaireItemWithTranslations = {
  linkId: '/mood-scale',
  text: 'How would you describe your mood today?',
  _text: withEs('¿Cómo describiría su estado de ánimo hoy?'),
  type: 'choice',
  required: true,
  answerOption: moodAnswerOptions,
};

const moodScoreObservationTemplate = buildScoreObservationTemplate({
  id: 'mood-scale-score-tmpl',
  totalScoreCode: MOOD_SCALE_RESULT_CODE,
  totalScoreSystem: SURVEY_SCORES_SYSTEM,
  display: 'Mood',
  scoredItems: [moodItem],
});

const URL = 'https://liza.health/fhir/questionnaire/mood-scale';

export const moodScaleQuestionnaire: QuestionnaireWithTranslations = {
  resourceType: 'Questionnaire',
  meta: {
    profile: ['http://hl7.org/fhir/uv/sdc/StructureDefinition/sdc-questionnaire'],
  },
  extension: [SEMVER_VERSION_ALGORITHM_EXTENSION, buildTemplateExtractExtension('mood-scale-score-tmpl')],
  contained: [moodScoreObservationTemplate],
  url: URL,
  // Internal instrument with no standard OID — mirror the canonical url as a FHIR identifier so
  // re-deploys upsert (identifier-based idempotency) rather than duplicate.
  identifier: [{ system: 'urn:ietf:rfc:3986', value: URL }],
  version: '1',
  name: 'MoodScale',
  language: 'en',
  title: 'Mood',
  _title: withEs('Estado de ánimo'),
  status: 'active',
  subjectType: ['Patient'],
  date: '2026-06-23',
  publisher: 'Liza Health',
  jurisdiction: [{ coding: [{ system: 'urn:iso:std:iso:3166', code: 'US' }] }],
  item: [moodItem],
};
